import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable, finalize, shareReplay, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ProfileEnum } from '../enums/role.enum';
import { AuthResponse } from '../interfaces/auth.interface';
import { LoginRequest } from '../interfaces/login.interface';
import { RegisterRequest } from '../interfaces/user.interface';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly TOKEN_KEY = 'jwtToken';
    private readonly ROLE_KEY = 'roles';
    private readonly CURRENT_USER_KEY = 'currentUser';
    private readonly API_URL = environment.apiUrls.authApi;

    private isAuthenticated$ = new BehaviorSubject<boolean>(false);

    private currentUser$ = new BehaviorSubject<{ name: string; email: string } | null>(null);

    private readonly currentUserSubject: BehaviorSubject<any>;
    public currentUser: Observable<any>;

    /**
     * Garante que somente uma requisição de refresh
     * seja executada quando várias requests receberem 401
     * simultaneamente.
     */
    private refreshRequest$: Observable<AuthResponse> | null = null;

    constructor(
        private http: HttpClient,
        private router: Router
    ) {
        const storedUser = JSON.parse(localStorage.getItem(this.CURRENT_USER_KEY) || sessionStorage.getItem(this.CURRENT_USER_KEY) || '{}');

        this.currentUserSubject = new BehaviorSubject<any>(storedUser);
        this.currentUser = this.currentUserSubject.asObservable();

        if (this.getToken()) {
            this.currentUserSubject.next(storedUser);
        }
    }

    register(request: RegisterRequest) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });

        request.roles = ProfileEnum.getFullAccess();

        return this.http.post<any>(`${this.API_URL}/users/register`, request, { headers });
    }

    login(request: LoginRequest): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });

        const body = {
            email: request.email,
            password: request.password
        };

        return this.http
            .post<any>(`${this.API_URL}/auth/login`, body, {
                headers,
                withCredentials: true
            })
            .pipe(
                tap((response: AuthResponse) => {
                    const decodedToken: any = jwtDecode(response.accessToken);
                    this.save(response, request.rememberMe, decodedToken);
                })
            );
    }

    /**
     * Renova o access token.
     *
     * O refresh token NÃO é enviado manualmente.
     * O browser envia automaticamente o HttpOnly Cookie
     * porque withCredentials=true.
     */
    refresh(): Observable<AuthResponse> {
        if (this.refreshRequest$) {
            return this.refreshRequest$;
        }

        this.refreshRequest$ = this.http
            .post<AuthResponse>(
                `${this.API_URL}/auth/refresh`,
                {},
                {
                    withCredentials: true
                }
            )
            .pipe(
                tap((response: AuthResponse) => {
                    const decodedToken: any = jwtDecode(response.accessToken);

                    const rememberMe = localStorage.getItem(this.TOKEN_KEY) !== null;

                    this.save(response, rememberMe, decodedToken);
                }),

                finalize(() => {
                    this.refreshRequest$ = null;
                }),

                shareReplay(1)
            );

        return this.refreshRequest$;
    }

    logout(): void {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });

        this.http
            .post<boolean>(
                `${this.API_URL}/auth/logout`,
                {},
                {
                    headers,
                    withCredentials: true
                }
            )
            .subscribe({
                next: () => {
                    this.clear();
                    this.currentUserSubject.next({});
                    this.router.navigate(['/public/login']);
                },
                error: (err) => {
                    console.error('Logout error:', err);
                    this.clear();
                    this.currentUserSubject.next({});
                    this.router.navigate(['/public/login']);
                }
            });
    }

    resendConfirmation(token: string) {
        return this.http.get<any>(`${this.API_URL}/users/resend?token=${token}`);
    }

    getToken(): string | null {
        return localStorage.getItem(this.TOKEN_KEY) || sessionStorage.getItem(this.TOKEN_KEY);
    }

    getCurrentUserInfo() {
        return localStorage.getItem(this.CURRENT_USER_KEY) || sessionStorage.getItem(this.CURRENT_USER_KEY);
    }

    clearSessionAndRedirect(): void {
        this.clear();
        this.currentUserSubject.next({});

        this.router.navigate(['/public/login'], {
            queryParams: {
                reason: 'session-expired'
            }
        });
    }

    getRoles(): string[] {
        const rolesString = localStorage.getItem(this.ROLE_KEY);

        try {
            return rolesString ? JSON.parse(rolesString) : [];
        } catch {
            return [];
        }
    }

    private clear(): void {
        localStorage.removeItem(this.TOKEN_KEY);
        localStorage.removeItem(this.CURRENT_USER_KEY);
        localStorage.removeItem(this.ROLE_KEY);

        sessionStorage.removeItem(this.TOKEN_KEY);
        sessionStorage.removeItem(this.CURRENT_USER_KEY);
        sessionStorage.removeItem(this.ROLE_KEY);
    }

    private save(user: AuthResponse, rememberMe: boolean, decodedToken: any): void {
        if (!user.accessToken) {
            return;
        }

        const userToSave = {
            ...user,
            user: {
                ...user.user,
                tenantId: parseInt(decodedToken.jti)
            }
        };

        localStorage.removeItem('countResended');
        localStorage.removeItem('resendCooldown');

        if (rememberMe) {
            localStorage.setItem(this.TOKEN_KEY, user.accessToken);

            localStorage.setItem(this.CURRENT_USER_KEY, JSON.stringify(userToSave));

            localStorage.setItem(this.ROLE_KEY, JSON.stringify(decodedToken.roles));
        } else {
            sessionStorage.setItem(this.TOKEN_KEY, user.accessToken);

            sessionStorage.setItem(this.CURRENT_USER_KEY, JSON.stringify(userToSave));

            /**
             * Mantido exatamente como estava:
             * roles continuam no localStorage.
             */
            localStorage.setItem(this.ROLE_KEY, JSON.stringify(decodedToken.roles));
        }

        this.currentUserSubject.next(userToSave);
    }
}
